import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuotes } from "../context/QuotesContext";
import BrandLogo from "../components/BrandLogo";
import { Button } from "../components/ui/button";
import {
  Plus,
  FileText,
  Pencil,
  Copy,
  Trash2,
  ArrowLeft,
  Calendar,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "../hooks/use-toast";

const QuotesList = () => {
  const navigate = useNavigate();
  const { quotes, createQuote, duplicateQuote, deleteQuote } = useQuotes();
  const [del, setDel] = React.useState(null);

  const handleCreate = () => {
    const q = createQuote();
    toast({ title: "Cotización creada" });
    navigate(`/cotizacion/${q.id}`);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/80 backdrop-blur">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-6 py-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/")}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <BrandLogo className="h-8 w-auto" />
          </div>
          <Button size="sm" onClick={handleCreate} className="gap-1.5 bg-blue-600 hover:bg-blue-700">
            <Plus className="h-4 w-4" /> Nueva cotización
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-8">
        <div className="mb-8">
          <h1 className="font-display text-3xl font-bold text-slate-900">Cotizaciones</h1>
          <p className="mt-1 text-slate-500">
            Crea, edita y descarga cotizaciones en PDF para enviarlas por WhatsApp.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {quotes.map((q) => (
            <div
              key={q.id}
              className="group overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition hover:shadow-lg"
            >
              <div className="flex h-28 items-center justify-center bg-gradient-to-br from-[#1B3FB8] to-[#2E6BFF]">
                <FileText className="h-10 w-10 text-white/90" />
              </div>
              <div className="p-5">
                <h3 className="truncate font-display text-lg font-bold text-slate-900">{q.name}</h3>
                <p className="mt-1 flex items-center gap-1.5 text-xs text-slate-400">
                  <Calendar className="h-3.5 w-3.5" /> {q.createdAt}
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button size="sm" className="flex-1 gap-1.5 bg-blue-600 hover:bg-blue-700" onClick={() => navigate(`/cotizacion/${q.id}`)}>
                    <Pencil className="h-4 w-4" /> Editar
                  </Button>
                  <Button size="sm" variant="outline" className="gap-1.5" onClick={() => { duplicateQuote(q.id); toast({ title: "Cotización duplicada" }); }}>
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="outline" className="gap-1.5 text-red-600 hover:text-red-600" onClick={() => setDel(q)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
          <button
            onClick={handleCreate}
            className="flex min-h-[220px] flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-slate-300 bg-white/50 text-slate-400 transition hover:border-blue-400 hover:text-blue-500"
          >
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-blue-50 text-blue-500">
              <Plus className="h-7 w-7" />
            </span>
            <span className="font-medium">Nueva cotización</span>
          </button>
        </div>
      </main>

      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar esta cotización?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará "{del?.name}" de forma permanente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => { deleteQuote(del.id); toast({ title: "Cotización eliminada" }); setDel(null); }}>
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default QuotesList;
